import { useEffect, useMemo, useState } from 'react';
import {
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  parseISO,
} from 'date-fns';
import styled from 'styled-components';

import theme from '@styles/theme';

type PostHeaderProps = {
  postRoleName: string | null | undefined;
  postProjectName: string | null | undefined;
  postProjectLocation: LocationModel | null | undefined;
  postStatus: JobsBoardApplicationStatus | undefined;
  postCloseDate: string | null | undefined;
  className?: string;
};
export const PostHeader = ({
  postRoleName,
  postProjectName,
  postProjectLocation,
  postStatus,
  postCloseDate,
  className,
}: PostHeaderProps) => {
  const [now, setNow] = useState(new Date());

  const postCloseDateTime = useMemo(
    () => (postCloseDate ? parseISO(postCloseDate) : null),
    [postCloseDate]
  );

  const deadlineTimeLeft = useMemo(() => {
    if (!postCloseDateTime) {
      return null;
    }

    const diffInSeconds = differenceInSeconds(postCloseDateTime, now);
    const diffInMinutes = differenceInMinutes(postCloseDateTime, now);
    const diffInHours = differenceInHours(postCloseDateTime, now);

    return {
      seconds: diffInSeconds,
      minutes: diffInMinutes,
      hours: diffInHours,
    };
  }, [postCloseDateTime, now]);

  useEffect(() => {
    const now = new Date();

    const shouldTriggerNowLogic =
      postStatus === 'open' && !!postCloseDateTime && postCloseDateTime > now;
    if (!shouldTriggerNowLogic) return;

    const msUntil24hWindow =
      postCloseDateTime.getTime() - 24 * 60 * 60 * 1000 - now.getTime();

    let interval: NodeJS.Timeout;
    let timeout: NodeJS.Timeout;

    if (msUntil24hWindow <= 0) {
      // Already inside 24h window
      interval = setInterval(() => {
        setNow(new Date());
      }, 1000);
    } else {
      // Wait until entering 24h window
      timeout = setTimeout(() => {
        interval = setInterval(() => {
          setNow(new Date());
        }, 1000);
      }, msUntil24hWindow);
    }

    return () => {
      if (interval) clearInterval(interval);
      if (timeout) clearTimeout(timeout);
    };
  }, [postCloseDateTime, postStatus]);

  const tagConfig = useMemo(() => {
    if (postStatus === 'closed') {
      return {
        text: 'Closed',
        width: '59px',
        backgroundColor: theme.palette.text[9],
      };
    }
    if (postStatus === 'submitted') {
      return {
        text: 'Submitted',
        width: '79px',
        backgroundColor: theme.palette.stroke[5],
      };
    }

    if (
      Number.isFinite(deadlineTimeLeft?.hours) &&
      deadlineTimeLeft?.hours < 24 &&
      deadlineTimeLeft?.seconds >= 0
    ) {
      let text = `Closes in ${deadlineTimeLeft?.hours} hours`;
      let width = '124px';

      if (deadlineTimeLeft?.hours === 1) {
        text = `Closes in 1 hour`;
        width = '106px';
      } else if (deadlineTimeLeft?.hours === 0) {
        if (deadlineTimeLeft?.minutes > 1) {
          text = `Closes in ${deadlineTimeLeft?.minutes} minutes`;
          width = '134px';
        } else if (deadlineTimeLeft?.minutes === 1) {
          text = `Closes in 1 minute`;
          width = '119px';
        } else {
          text = 'Closes in less than a minute';
          width = '175px';
        }
      }
      return {
        text,
        width,
        backgroundColor: theme.palette.stroke[4],
      };
    }
    return null;
  }, [deadlineTimeLeft, postStatus]);

  const projectLocationDisplay = useMemo(() => {
    if (!postProjectLocation) {
      return null;
    }
    return [postProjectLocation?.city, postProjectLocation?.state]
      .filter(Boolean)
      .join(', ');
  }, [postProjectLocation]);

  return (
    <StyledContainer tagConfig={tagConfig} className={className}>
      <div className='post-title'>
        <div className='post-title-text text-wrap-anywhere'>
          {postRoleName || '-'}
        </div>
        {!!tagConfig && <div className='post-title-tag'>{tagConfig?.text}</div>}
      </div>
      <div className='post-subtitle'>
        <div className='text-wrap-anywhere'>{postProjectName || '-'}</div>
        {!!projectLocationDisplay && (
          <>
            <div className='dot-separator' />
            <div className='project-location'>{projectLocationDisplay}</div>
          </>
        )}
      </div>
    </StyledContainer>
  );
};

type StyledContainerProps = {
  tagConfig: TagConfig | null;
};
const StyledContainer = styled.div<StyledContainerProps>`
  display: flex;
  flex-direction: column;
  gap: 4px;

  .post-title {
    display: flex;
    gap: 8px;
    justify-content: space-between;
    align-items: flex-start;

    .post-title-text {
      font-weight: 600;
      font-size: 20px;
      line-height: 24px;
      width: ${({ tagConfig }) =>
        tagConfig?.width ? `calc(100% - 8px - ${tagConfig.width})` : '100%'};
    }

    .post-title-tag {
      width: ${({ tagConfig }) => tagConfig?.width};
      background-color: ${({ tagConfig }) => tagConfig?.backgroundColor};
      color: #fff;
      padding: 4px 8px;
      font-size: 12px;
      line-height: 16px;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;

      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
    }
  }
  .post-subtitle {
    font-size: 14px;
    line-height: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    @media (max-width: 768px) {
      font-size: 16px;
      line-height: 24px;
    }

    .dot-separator {
      width: 4px;
      height: 4px;
      background-color: ${theme.palette.text[9]};
      border-radius: 50%;
    }

    .project-location {
      color: ${theme.palette.text[10]};
    }
  }
`;

type TagConfig = {
  text: string;
  width: string;
  backgroundColor: string;
};
